import type { CardId } from './types.js';

/**
 * Uses Web Crypto, which is present in both Node ≥19 and every target browser,
 * so the engine stays isomorphic — the client imports the same module for
 * non-authoritative UI hinting.
 */
const webcrypto: Crypto = globalThis.crypto;

export function randomInt(maxExclusive: number): number {
  if (maxExclusive <= 0) throw new Error('maxExclusive must be positive');
  // Rejection sampling, so the modulo bias that would quietly skew a shuffle
  // never gets a chance.
  const limit = Math.floor(0x100000000 / maxExclusive) * maxExclusive;
  const buf = new Uint32Array(1);
  let value: number;
  do {
    webcrypto.getRandomValues(buf);
    value = buf[0]!;
  } while (value >= limit);
  return value % maxExclusive;
}

export function randomHex(bytes: number): string {
  const buf = new Uint8Array(bytes);
  webcrypto.getRandomValues(buf);
  return [...buf].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Fisher-Yates over a CSPRNG (§5.1). */
export function shuffle<T>(items: readonly T[], rand: (n: number) => number = randomInt): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = rand(i + 1);
    const a = out[i]!;
    out[i] = out[j]!;
    out[j] = a;
  }
  return out;
}

export interface DeckCommitment {
  /** `SHA-256(deck_order ‖ nonce)`, published at the start of the hand. */
  commit: string;
  /** Published at hand end, alongside the order, so anyone can recompute. */
  nonce: string;
}

export async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await webcrypto.subtle.digest('SHA-256', data);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function commitPayload(order: readonly CardId[], nonce: string): string {
  return `${order.join(',')}|${nonce}`;
}

/**
 * Commit–reveal deck verification (§5.1).
 *
 * Deliberately *not* called "provably fair". This proves the deck order was
 * fixed before play began and not reordered mid-hand. It does not prove the
 * shuffle was uniformly random, and it does not stop a compromised server from
 * choosing a favourable order before committing. The stronger claim is not
 * supportable.
 */
export async function commitDeck(order: readonly CardId[]): Promise<DeckCommitment> {
  const nonce = randomHex(16);
  return { commit: await sha256Hex(commitPayload(order, nonce)), nonce };
}

export async function verifyDeck(
  order: readonly CardId[],
  nonce: string,
  commit: string,
): Promise<boolean> {
  return (await sha256Hex(commitPayload(order, nonce))) === commit;
}
